import { useNavigate } from '@tanstack/react-router'
import { Button } from 'antd'
import { Card } from '../components/Card'
import { QrCodePanel } from '../components/QrCodePanel'
import { StepBadge } from '../components/StepBadge'

/**
 * Step 2 of 4: Set Up Authenticator (QR Code Scan).
 * Ported from the web client's `/login/setup-authenticator` page.
 */
export function SetupAuthenticatorPage() {
  const navigate = useNavigate()

  return (
    <Card>
      {/* 1. Accessible Step Badge */}
      <StepBadge step={2} />

      {/* 2. Heading */}
      <header className="flex flex-col gap-2">
        <h1 className="text-[22px] font-bold tracking-tight text-brand-dark-text">
          Set up your authenticator
        </h1>
        <p className="text-[13px] text-brand-copyright leading-relaxed">
          Scan the QR code with Google Authenticator, Authy, or any TOTP app to connect your
          account.
        </p>
      </header>

      <div className="bg-brand-bg-light border border-brand-border-light/50 rounded-2xl p-5">
        {/* 3. QR Code (fetched from POST /auth/mfa/setup) */}
        <div className="flex justify-center items-center mb-4">
          <QrCodePanel />
        </div>

        {/* 4. Semantic Ordered Instructions */}
        <section className="flex flex-col gap-3" aria-label="Authenticator setup instructions">
          <h2 className="font-bold text-[12px] text-brand-dark-text tracking-tight">How to scan</h2>

          <ol className="flex flex-col gap-3">
            <li className="flex gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan font-bold text-xs shrink-0 select-none">
                1
              </span>
              <div className="flex flex-col gap-0.5">
                <strong className="text-[11px] font-bold text-brand-dark-text leading-tight">
                  Install an authenticator app
                </strong>
                <span className="text-[11px] text-brand-copyright leading-relaxed">
                  Google Authenticator, Microsoft Authenticator, or Authy (all free).
                </span>
              </div>
            </li>

            <li className="flex gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan font-bold text-xs shrink-0 select-none">
                2
              </span>
              <div className="flex flex-col gap-0.5">
                <strong className="text-[11px] font-bold text-brand-dark-text leading-tight">
                  Tap + or Add account
                </strong>
                <span className="text-[11px] text-brand-copyright leading-relaxed">
                  Choose &quot;Scan a QR code&quot; when prompted.
                </span>
              </div>
            </li>

            <li className="flex gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan font-bold text-[11px] shrink-0 select-none">
                3
              </span>
              <div className="flex flex-col gap-0.5">
                <strong className="text-[11px] font-bold text-brand-dark-text leading-tight">
                  Point your camera here
                </strong>
                <span className="text-[11px] text-brand-copyright leading-relaxed">
                  The app connects automatically.
                </span>
              </div>
            </li>
          </ol>
        </section>

        {/* 5. Manual key alternative */}
        <footer className="flex flex-col items-start mt-3">
          <Button
            type="link"
            iconPosition="end"
            className="text-xs font-semibold !px-0"
            onClick={() => navigate({ to: '/login/setup-authenticator/manual-key' })}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            }
          >
            Can&apos;t scan? Enter code manually
          </Button>
        </footer>
      </div>

      {/* 6. Advance to the authenticator-app code entry (MFA setup confirm) */}
      <Button
        type="primary"
        block
        size="large"
        id="btn-setup-continue"
        onClick={() => navigate({ to: '/login/verify-app' })}
      >
        Continue
      </Button>
    </Card>
  )
}
